import { useState } from "react";
import { Link } from "react-router-dom";
import type { CronJobView } from "@agentdash/shared";
import { describeCron, nextFireDate } from "../../lib/cron-format.js";
import { formatRelative, formatTimeUntil } from "../../lib/time.js";
import { useNow } from "../../lib/use-now.js";
import {
  usePauseCronJob,
  useResumeCronJob,
  useRunCronJobNow,
  useSetCronJobWrapped,
} from "./hooks.js";
import { controlState } from "./control-state.js";
import { ScheduleEditor } from "./ScheduleEditor.js";

const ctrlButton: React.CSSProperties = {
  background: "transparent",
  color: "var(--muted)",
  border: "1px solid var(--border)",
  borderRadius: 3,
  padding: "3px 8px",
  fontSize: 11,
  cursor: "pointer",
};

export function CronRow({ job }: { job: CronJobView }) {
  const summary = describeCron(job.schedule);
  const [editing, setEditing] = useState(false);
  const now = useNow();
  const nextFire = job.enabled
    ? nextFireDate(job.schedule, job.agent_timezone)
    : null;
  const timeUntil = nextFire ? formatTimeUntil(nextFire, now) : null;
  const pause = usePauseCronJob();
  const resume = useResumeCronJob();
  const runNow = useRunCronJobNow();
  const setWrapped = useSetCronJobWrapped();

  const busy =
    pause.isPending ||
    resume.isPending ||
    runNow.isPending ||
    setWrapped.isPending;

  // Surface a failed control action instead of swallowing it: each mutation is
  // fire-and-forget, so without this a 4xx/5xx leaves the button re-enabled and
  // the operator assuming the pause/run/wrap took effect. (ScheduleEditor shows
  // its own setSchedule error.)
  //
  // The `??` coalesce only ever shows one error, but a stale failure from a
  // previous action would otherwise linger beneath a later *success* (React
  // Query only resets a mutation's own error on its next mutate()). So every
  // control resets its siblings before firing — whatever is on screen always
  // belongs to the most recent action.
  const mutations: { reset: () => void }[] = [
    pause,
    resume,
    runNow,
    setWrapped,
  ];
  const resetOthers = (keep: { reset: () => void }) => {
    for (const m of mutations) if (m !== keep) m.reset();
  };
  const requestError =
    pause.error ?? resume.error ?? runNow.error ?? setWrapped.error;

  // For wrapped jobs we have a definitive "is running" signal: a runs row
  // tagged with agent.cron_job_id that hasn't reached an end span yet. That
  // trumps the 5-min cron_commands lookback because real runs can last much
  // longer than 5 minutes. For unwrapped jobs we fall back to that lookback.
  const lastRun =
    job.latest_command?.type === "run_now" ? job.latest_command : null;
  const runQueued = lastRun?.status === "pending";
  const runFailed = lastRun?.status === "failed";
  const runRunning = job.live_run != null || lastRun?.status === "applied";
  const runInFlight = runQueued || runRunning;

  // Per-button pending: stays true from click → agent apply → next snapshot
  // (i.e. until the underlying field on the job has actually flipped).
  // Closes the silent-window between "POST returned 200" and "state visible".
  const control = controlState(job);
  const pausePending = pause.isPending || control.pausePending;
  const resumePending = resume.isPending || control.resumePending;
  const wrapPending = setWrapped.isPending || control.wrapPending;

  const dotColor = !job.enabled
    ? "var(--muted)"
    : runFailed
    ? "var(--err)"
    : runInFlight
    ? "var(--running)"
    : "var(--accent)";
  const dotGlow = runInFlight ? "0 0 6px var(--running)" : "none";

  return (
    <div
      className="cron-row"
      style={{
        padding: "10px 16px",
        borderTop: "1px solid var(--border)",
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 12,
        alignItems: "center",
        opacity: job.enabled ? 1 : 0.55,
      }}
    >
      <div
        title={
          runQueued
            ? "Queued — host-agent will pick it up within ~5s"
            : runRunning
            ? "Running — dispatched by host-agent"
            : runFailed
            ? `Failed: ${lastRun?.error ?? "(no error message)"}`
            : !job.enabled
            ? "Paused"
            : "Idle"
        }
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          background: dotColor,
          boxShadow: dotGlow,
        }}
      />
      <div style={{ display: "grid", gap: 2, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {summary}{" "}
          <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 12 }}>
            ({job.schedule})
          </span>
          {timeUntil && nextFire && (
            <span
              title={`Next fire: ${nextFire.toLocaleString()} (parsed in ${
                job.agent_timezone
                  ? `host TZ: ${job.agent_timezone}`
                  : "your browser's TZ — agent hasn't reported a host TZ yet"
              })`}
              style={{
                fontWeight: 400,
                color: "var(--muted)",
                fontSize: 12,
                marginLeft: 8,
              }}
            >
              · {timeUntil}
            </span>
          )}
        </div>
        <div
          title={job.command}
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12,
            color: "var(--muted)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {job.relative_path
            ? `${job.relative_path} • ${job.command}`
            : job.command}
        </div>
        {!job.wrapped && (
          <div style={{ fontSize: 11, color: "var(--warn)" }}>
            Prefix the command with{" "}
            <code
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 3,
                padding: "1px 4px",
              }}
            >
              run-monitored --
            </code>{" "}
            to surface runs of this job under /runs and /live
            {job.agent_has_run_monitored
              ? " (use the Wrap button to do it from here)."
              : " — but first install the run-monitored binary on the host (the agent reports it's missing)."}
          </div>
        )}
      </div>
      <div
        className="cron-row-controls"
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          fontSize: 11,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        {job.wrapped ? (
          <span
            title="Wrapped with run-monitored — runs show up in the live view"
            style={badgeStyle("var(--accent)")}
          >
            wrapped
          </span>
        ) : (
          <span
            title="Not wrapped with run-monitored — see the hint below the command for how to enable."
            style={badgeStyle("var(--warn)")}
          >
            unwrapped
          </span>
        )}
        {!job.enabled && (
          <span style={badgeStyle("var(--warn)")}>paused</span>
        )}
        {runQueued && (
          <span
            title="Queued — host-agent picks it up within ~5s"
            style={badgeStyle("var(--running)")}
          >
            queued…
          </span>
        )}
        {runRunning && job.live_run && (
          <Link
            to={`/runs/${job.live_run.trace_id}`}
            title={`Running since ${formatRelative(job.live_run.started_at)} — click to open the run`}
            style={{ ...badgeStyle("var(--running)"), textDecoration: "none" }}
          >
            running
          </Link>
        )}
        {runRunning && !job.live_run && lastRun?.applied_at && (
          <span
            title={`Dispatched ${formatRelative(lastRun.applied_at)} — for unwrapped commands the dashboard can't detect completion; the indicator clears after 5 min`}
            style={badgeStyle("var(--running)")}
          >
            running
          </span>
        )}
        {runFailed && (
          <span
            title={lastRun?.error ?? "Run failed"}
            style={badgeStyle("var(--err)")}
          >
            failed
          </span>
        )}

        {/* Controls — enqueue commands; the agent applies them within ~30s.
            Hide Run-now while a run is in flight so the user can't enqueue a
            duplicate on top of a running one. */}
        {!runInFlight && (
          <button
            type="button"
            onClick={() => {
              resetOthers(runNow);
              runNow.mutate(job.cron_job_id);
            }}
            disabled={busy}
            style={ctrlButton}
            title={
              job.wrapped
                ? "Run this command immediately on the host (separate from the schedule)"
                : "Run this command immediately on the host. Results won't appear here — line isn't wrapped with run-monitored."
            }
          >
            Run now
          </button>
        )}
        {job.enabled ? (
          <button
            type="button"
            onClick={() => {
              resetOthers(pause);
              pause.mutate(job.cron_job_id);
            }}
            disabled={busy || pausePending}
            style={pendingStyle(pausePending)}
            title="Comment out the cron line so it stops firing"
          >
            {pausePending ? "Pause pending…" : "Pause"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              resetOthers(resume);
              resume.mutate(job.cron_job_id);
            }}
            disabled={busy || resumePending}
            style={pendingStyle(resumePending)}
            title="Uncomment the cron line"
          >
            {resumePending ? "Resume pending…" : "Resume"}
          </button>
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          disabled={busy}
          style={ctrlButton}
          title="Edit the schedule fields of this cron line"
        >
          Edit
        </button>
        {(() => {
          // Block Wrap (but not Unwrap) when the agent reports run-monitored
          // is not installed — wrapping in that state silently breaks the
          // cron command with "/bin/sh: run-monitored: not found".
          const wrapBlocked = !job.wrapped && !job.agent_has_run_monitored;
          const disabled = busy || wrapPending || wrapBlocked;
          const label = wrapPending
            ? job.wrapped
              ? "Unwrap pending…"
              : "Wrap pending…"
            : job.wrapped
            ? "Unwrap"
            : "Wrap";
          const title = wrapBlocked
            ? "run-monitored isn't installed on the agent's host. Install it (see tools/run-monitored/README.md) and the Wrap button will enable on the next snapshot."
            : job.wrapped
            ? "Remove the run-monitored wrapper from this cron line"
            : "Prefix the command with run-monitored -- so its runs show in /runs and /live";
          return (
            <button
              type="button"
              onClick={() => {
                resetOthers(setWrapped);
                setWrapped.mutate({
                  cronJobId: job.cron_job_id,
                  wrapped: !job.wrapped,
                });
              }}
              disabled={disabled}
              style={pendingStyle(wrapPending || wrapBlocked)}
              title={title}
            >
              {label}
            </button>
          );
        })()}
        {(requestError || control.agentError) && (
          <div
            role="alert"
            style={{
              flexBasis: "100%",
              textAlign: "right",
              color: "var(--err)",
              fontSize: 11,
            }}
          >
            {requestError
              ? String((requestError as Error).message ?? requestError)
              : control.agentError}
          </div>
        )}
      </div>

      {editing && (
        <ScheduleEditor
          cronJobId={job.cron_job_id}
          currentSchedule={job.schedule}
          agentTimezone={job.agent_timezone}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

function badgeStyle(color: string): React.CSSProperties {
  return {
    padding: "2px 6px",
    border: "1px solid var(--border)",
    borderRadius: 3,
    color,
  };
}

function pendingStyle(pending: boolean): React.CSSProperties {
  return {
    ...ctrlButton,
    opacity: pending ? 0.5 : 1,
    cursor: pending ? "not-allowed" : "pointer",
  };
}
